import { useState } from 'react';
import { Lock } from 'lucide-react';
import Button from '../ui/Button';
import { CheckBadge } from './ComplianceReportView';
import type { Checklist, Checkpoint, CheckpointStatus } from '../../types/compliance';

/**
 * A standard's checklist. Each checkpoint shows what the records say (the
 * automatic verdict) beside what the org says (its own answer). The two are
 * kept apart on purpose: the records are evidence, the answer is the org's
 * self-assessment, and an auditor should be able to see where they disagree.
 */

const STATUS_LABELS: Record<CheckpointStatus, string> = {
  unanswered: 'Not answered',
  compliant: 'Compliant',
  non_compliant: 'Not compliant',
  not_applicable: 'Not applicable',
};

const STATUS_CLASS: Record<CheckpointStatus, string> = {
  unanswered: 'bg-neutral-100 text-neutral-600',
  compliant: 'bg-green-100 text-green-800',
  non_compliant: 'bg-red-100 text-red-800',
  not_applicable: 'bg-neutral-100 text-neutral-700',
};

export type CheckpointSave = (key: string, data: {
  status: CheckpointStatus; clause_ref: string | null; note: string | null; evidence_url: string | null;
}) => Promise<void>;

function Row({ cp, onSave }: { cp: Checkpoint; onSave?: CheckpointSave }) {
  const [status, setStatus] = useState<CheckpointStatus>(cp.status);
  const [ref, setRef] = useState(cp.clause_ref ?? '');
  const [note, setNote] = useState(cp.note ?? '');
  const [url, setUrl] = useState(cp.evidence_url ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = status !== cp.status || ref !== (cp.clause_ref ?? '') || note !== (cp.note ?? '') || url !== (cp.evidence_url ?? '');

  const save = async () => {
    if (!onSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(cp.key, { status, clause_ref: ref.trim() || null, note: note.trim() || null, evidence_url: url.trim() || null });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 break-words text-sm font-medium text-neutral-900">
          {cp.clause_ref && <span className="mr-1 text-neutral-500">{cp.clause_ref}</span>}{cp.title}
        </p>
        {!onSave && <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[cp.status]}`}>{STATUS_LABELS[cp.status]}</span>}
      </div>
      <div className="mt-1 text-sm text-neutral-600">
        {cp.auto ? (
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-2">
            <span className="shrink-0"><CheckBadge state={cp.auto.state} /></span>
            <span className="break-words">{cp.auto.detail}</span>
          </div>
        ) : cp.hidden ? (
          <span className="inline-flex items-center gap-1 text-xs text-neutral-500"><Lock className="h-3.5 w-3.5" />Checked from records you do not hold the seat for.</span>
        ) : (
          <span className="text-xs text-neutral-500">Not in FactreeTech records — answered by hand.</span>
        )}
      </div>

      {onSave ? (
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[10rem_7rem_1fr]">
          <select aria-label={`Status: ${cp.title}`} value={status} onChange={e => setStatus(e.target.value as CheckpointStatus)}
            className="w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm">
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input aria-label={`Reference: ${cp.title}`} value={ref} onChange={e => setRef(e.target.value)} placeholder="Ref no."
            className="w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm" maxLength={64} />
          <input aria-label={`Evidence link: ${cp.title}`} value={url} onChange={e => setUrl(e.target.value)} placeholder="Link to evidence (optional)"
            className="w-full min-w-0 rounded-lg border border-neutral-300 px-2 py-1.5 text-sm" />
          <textarea aria-label={`Note: ${cp.title}`} value={note} onChange={e => setNote(e.target.value)} rows={2}
            placeholder="How you meet it, or what is still to do"
            className="w-full rounded-lg border border-neutral-300 px-2 py-1.5 text-sm sm:col-span-3" maxLength={4000} />
          {(dirty || error) && (
            <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
              <Button size="sm" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save'}</Button>
              {error && <span className="text-sm text-red-700">{error}</span>}
            </div>
          )}
        </div>
      ) : (cp.note || cp.evidence_url) ? (
        <div className="mt-2 space-y-1 text-sm text-neutral-700">
          {cp.note && <p className="whitespace-pre-line break-words">{cp.note}</p>}
          {cp.evidence_url && <a href={cp.evidence_url} target="_blank" rel="noreferrer" className="break-all text-primary-700 underline">{cp.evidence_url}</a>}
        </div>
      ) : null}
    </li>
  );
}

export default function ChecklistView({ checklist, onSave }: { checklist: Checklist; onSave?: CheckpointSave }) {
  const topics: string[] = [];
  for (const c of checklist.checkpoints) if (!topics.includes(c.topic)) topics.push(c.topic);
  const counts = checklist.checkpoints.reduce<Record<string, number>>((m, c) => ({ ...m, [c.status]: (m[c.status] || 0) + 1 }), {});
  return (
    <div>
      <p className="mb-2 text-sm text-neutral-600">{checklist.note}</p>
      <p className="mb-4 text-sm text-neutral-700">
        {counts.compliant || 0} compliant · {counts.non_compliant || 0} not compliant · {counts.not_applicable || 0} not applicable · {counts.unanswered || 0} not answered
      </p>
      {topics.map(t => (
        <section key={t} className="mb-4">
          <h3 className="mb-1 text-sm font-semibold text-neutral-900">{t}</h3>
          <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
            {checklist.checkpoints.filter(c => c.topic === t).map(c => (
              <Row key={`${c.key}:${c.updated_at ?? ''}`} cp={c} onSave={onSave} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
